import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
    IconButton, Chip, Alert, Tabs, Tab, FormControlLabel, Switch, Tooltip,
    CircularProgress, Snackbar, Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
    Key as KeyIcon, AttachMoney as MoneyIcon, Settings as SettingsIcon,
    Check as CheckIcon, Close as CloseIcon,
} from '@mui/icons-material';
import {
    getLLMProviders, getLLMConfigs, createLLMConfig, updateLLMConfig,
    deleteLLMConfig, getLLMApiKeys, createLLMApiKey, deleteLLMApiKey,
    getLLMPricing, createLLMPricing, updateLLMPricing, deleteLLMPricing,
} from '../../services/adminApi';
import { getTenants } from '../../services/adminApi';
import {
    LLMConfig, LLMConfigCreateRequest, LLMApiKey, ModelPricing,
    ModelPricingCreateRequest, ModelPricingUpdateRequest,
    AgentRequirement, Tenant,
} from '../../services/types';

const AGENT_NAMES = [
    'data_agent', 'target_schema', 'source_summarizer', 'researcher', 'mapper',
    'source_assignment', 'quality_assessment', 'correction', 'refiner', 'advisor',
    'schema_enrichment', 'readiness',
];

const PROVIDER_NAMES = ['openai', 'anthropic', 'gemini', 'ollama'];

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const ModelConfiguration: React.FC = () => {
    const queryClient = useQueryClient();
    const [tabIndex, setTabIndex] = useState(0);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const onMutationError = (e: any) => {
        // FastAPI returns `detail` as a string for HTTPException but as an
        // array of {loc,msg,type} for 422 validation errors — coerce to a
        // string so rendering it never throws "Objects are not valid as a
        // React child".
        const detail = e?.response?.data?.detail;
        let msg: string;
        if (Array.isArray(detail)) {
            msg = detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
        } else if (typeof detail === 'string') {
            msg = detail;
        } else if (detail) {
            msg = JSON.stringify(detail);
        } else {
            msg = e?.message || 'Request failed';
        }
        setErrorMsg(msg);
    };
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [keyDialogOpen, setKeyDialogOpen] = useState(false);
    const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState<LLMConfig | null>(null);
    const [editingPricing, setEditingPricing] = useState<ModelPricing | null>(null);
    // Form state — config
    const [formTenantId, setFormTenantId] = useState<string>('');
    const [formAgentName, setFormAgentName] = useState<string>('');
    const [formProvider, setFormProvider] = useState<string>('');
    const [formModel, setFormModel] = useState<string>('');
    const [formIsDefault, setFormIsDefault] = useState(false);
    const [formKeyTenantId, setFormKeyTenantId] = useState<string>('');
    const [formKeyProvider, setFormKeyProvider] = useState<string>('');
    const [formApiKey, setFormApiKey] = useState<string>('');
    // Form state — pricing
    const [pricingProvider, setPricingProvider] = useState<string>('');
    const [pricingModel, setPricingModel] = useState<string>('');
    const [pricingInputPrice, setPricingInputPrice] = useState<string>('');
    const [pricingOutputPrice, setPricingOutputPrice] = useState<string>('');
    const [pricingInputAbove, setPricingInputAbove] = useState<string>('');
    const [pricingOutputAbove, setPricingOutputAbove] = useState<string>('');
    const [pricingTierThreshold, setPricingTierThreshold] = useState<string>('');
    const [pricingContextWindow, setPricingContextWindow] = useState<string>('');
    const [pricingMaxOutput, setPricingMaxOutput] = useState<string>('');
    const [pricingSupportsTools, setPricingSupportsTools] = useState(true);
    const [pricingSupportsJson, setPricingSupportsJson] = useState(true);
    const [pricingIsActive, setPricingIsActive] = useState(true);

    // Queries
    const { data: providersData } = useQuery(['llm-providers'], getLLMProviders);
    const { data: configs = [], isLoading: configsLoading } = useQuery(['llm-configs'], () => getLLMConfigs());
    const { data: apiKeys = [], isLoading: keysLoading } = useQuery(['llm-keys'], () => getLLMApiKeys());
    const { data: pricing = [] } = useQuery(['llm-pricing'], () => getLLMPricing());
    const { data: tenants = [] } = useQuery(['tenants'], getTenants);
    const agentRequirements = providersData?.agent_requirements || {};

    // Mutations
    const createConfigMut = useMutation(
        createLLMConfig,
        { onSuccess: () => { queryClient.invalidateQueries(['llm-configs']); setConfigDialogOpen(false); }, onError: onMutationError },
    );
    const updateConfigMut = useMutation(
        ({ id, data }: { id: number; data: any }) => updateLLMConfig(id, data),
        { onSuccess: () => { queryClient.invalidateQueries(['llm-configs']); setConfigDialogOpen(false); }, onError: onMutationError },
    );
    const deleteConfigMut = useMutation(
        deleteLLMConfig,
        { onSuccess: () => queryClient.invalidateQueries(['llm-configs']), onError: onMutationError },
    );
    const createKeyMut = useMutation(
        createLLMApiKey,
        { onSuccess: () => { queryClient.invalidateQueries(['llm-keys']); setKeyDialogOpen(false); }, onError: onMutationError },
    );
    const deleteKeyMut = useMutation(
        deleteLLMApiKey,
        { onSuccess: () => queryClient.invalidateQueries(['llm-keys']), onError: onMutationError },
    );
    const createPricingMut = useMutation(
        createLLMPricing,
        { onSuccess: () => { queryClient.invalidateQueries(['llm-pricing']); setPricingDialogOpen(false); }, onError: onMutationError },
    );
    const updatePricingMut = useMutation(
        ({ id, data }: { id: number; data: ModelPricingUpdateRequest }) => updateLLMPricing(id, data),
        { onSuccess: () => { queryClient.invalidateQueries(['llm-pricing']); setPricingDialogOpen(false); }, onError: onMutationError },
    );
    const deletePricingMut = useMutation(
        deleteLLMPricing,
        { onSuccess: () => queryClient.invalidateQueries(['llm-pricing']), onError: onMutationError },
    );

    const openCreatePricing = () => {
        setEditingPricing(null);
        setPricingProvider('');
        setPricingModel('');
        setPricingInputPrice('');
        setPricingOutputPrice('');
        setPricingInputAbove('');
        setPricingOutputAbove('');
        setPricingTierThreshold('');
        setPricingContextWindow('');
        setPricingMaxOutput('');
        setPricingSupportsTools(true);
        setPricingSupportsJson(true);
        setPricingIsActive(true);
        setPricingDialogOpen(true);
    };

    const openEditPricing = (p: ModelPricing) => {
        setEditingPricing(p);
        setPricingProvider(p.provider_name);
        setPricingModel(p.model_name);
        setPricingInputPrice(String(p.input_price_per_mtok));
        setPricingOutputPrice(String(p.output_price_per_mtok));
        setPricingInputAbove(p.input_price_above_tier != null ? String(p.input_price_above_tier) : '');
        setPricingOutputAbove(p.output_price_above_tier != null ? String(p.output_price_above_tier) : '');
        setPricingTierThreshold(p.tier_threshold_tokens != null ? String(p.tier_threshold_tokens) : '');
        setPricingContextWindow(String(p.context_window));
        setPricingMaxOutput(p.max_output_tokens != null ? String(p.max_output_tokens) : '');
        setPricingSupportsTools(p.supports_tools);
        setPricingSupportsJson(p.supports_json_mode);
        setPricingIsActive(p.is_active);
        setPricingDialogOpen(true);
    };

    const handleSavePricing = () => {
        if (editingPricing) {
            const data: ModelPricingUpdateRequest = {
                input_price_per_mtok: parseFloat(pricingInputPrice),
                output_price_per_mtok: parseFloat(pricingOutputPrice),
                input_price_above_tier: pricingInputAbove ? parseFloat(pricingInputAbove) : null,
                output_price_above_tier: pricingOutputAbove ? parseFloat(pricingOutputAbove) : null,
                tier_threshold_tokens: pricingTierThreshold ? parseInt(pricingTierThreshold) : null,
                context_window: parseInt(pricingContextWindow),
                max_output_tokens: pricingMaxOutput ? parseInt(pricingMaxOutput) : null,
                supports_tools: pricingSupportsTools,
                supports_json_mode: pricingSupportsJson,
                is_active: pricingIsActive,
            };
            updatePricingMut.mutate({ id: editingPricing.pricing_id, data });
        } else {
            const data: ModelPricingCreateRequest = {
                provider_name: pricingProvider,
                model_name: pricingModel,
                input_price_per_mtok: parseFloat(pricingInputPrice),
                output_price_per_mtok: parseFloat(pricingOutputPrice),
                context_window: parseInt(pricingContextWindow),
                input_price_above_tier: pricingInputAbove ? parseFloat(pricingInputAbove) : null,
                output_price_above_tier: pricingOutputAbove ? parseFloat(pricingOutputAbove) : null,
                tier_threshold_tokens: pricingTierThreshold ? parseInt(pricingTierThreshold) : null,
                supports_tools: pricingSupportsTools,
                supports_json_mode: pricingSupportsJson,
                max_output_tokens: pricingMaxOutput ? parseInt(pricingMaxOutput) : null,
                is_active: pricingIsActive,
            };
            createPricingMut.mutate(data);
        }
    };

    const openCreateConfig = () => {
        setEditingConfig(null);
        setFormTenantId('');
        setFormAgentName('');
        setFormProvider('');
        setFormModel('');
        setFormIsDefault(false);
        setConfigDialogOpen(true);
    };

    const openEditConfig = (config: LLMConfig) => {
        setEditingConfig(config);
        setFormTenantId(config.tenant_id || '');
        setFormAgentName(config.agent_name || '');
        setFormProvider(config.provider_name);
        setFormModel(config.model_name);
        setFormIsDefault(config.is_default);
        setConfigDialogOpen(true);
    };

    const handleSaveConfig = () => {
        const data: LLMConfigCreateRequest = {
            tenant_id: formTenantId || null,
            agent_name: formAgentName || null,
            provider_name: formProvider,
            model_name: formModel,
            is_default: formIsDefault,
        };
        if (editingConfig) {
            updateConfigMut.mutate({ id: editingConfig.config_id, data: { provider_name: formProvider, model_name: formModel, is_default: formIsDefault } });
        } else {
            createConfigMut.mutate(data);
        }
    };

    const handleSaveKey = () => {
        createKeyMut.mutate({
            tenant_id: formKeyTenantId || null,
            provider_name: formKeyProvider,
            api_key: formApiKey,
        });
        setFormApiKey('');
    };

    const getTenantName = (tenantId: string | null) => {
        if (!tenantId) return 'Global';
        const tenant = tenants.find((t: Tenant) => t.tenant_id === tenantId);
        return tenant?.name || tenantId;
    };

    const getCapabilityBadges = (providerName: string, modelName: string) => {
        const model = pricing.find((p: ModelPricing) => p.provider_name === providerName && p.model_name === modelName);
        if (!model) return null;
        return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {model.supports_tools && <Chip label="Tools" size="small" color="primary" variant="outlined" />}
                {model.supports_json_mode && <Chip label="JSON" size="small" color="secondary" variant="outlined" />}
                <Chip label={`${(model.context_window / 1000).toFixed(0)}K ctx`} size="small" variant="outlined" />
                <Chip label={`$${model.input_price_per_mtok}/$${model.output_price_per_mtok} /MTok`} size="small" variant="outlined" />
            </Box>
        );
    };

    const capabilityError: string | null = (() => {
        if (!formProvider || !formModel) return null;
        const model = pricing.find(
            (p: ModelPricing) => p.provider_name === formProvider && p.model_name === formModel,
        );
        if (!model) {
            return `No pricing/capability entry for ${formProvider}/${formModel}. Add it under "Model Pricing" first so cost tracking and capability checks work.`;
        }
        const req = formAgentName ? agentRequirements[formAgentName] : null;
        if (!req) return null;
        if (req.needs_tools && !model.supports_tools) return `Model does not support tool calling, required by ${formAgentName}.`;
        if (req.needs_json_mode && !model.supports_json_mode) return `Model does not support JSON mode, required by ${formAgentName}.`;
        if (model.context_window < req.min_context) return `Model context ${(model.context_window / 1000).toFixed(0)}K is below the ${(req.min_context / 1000).toFixed(0)}K required by ${formAgentName}.`;
        return null;
    })();

    return (
        <Box>
            <Typography variant="h5" gutterBottom>Model Configuration</Typography>
            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
                <Tab icon={<SettingsIcon />} label="Model Configs" iconPosition="start" />
                <Tab icon={<KeyIcon />} label="API Keys" iconPosition="start" />
                <Tab icon={<MoneyIcon />} label="Model Pricing" iconPosition="start" />
            </Tabs>

            {/* Tab 0: Model Configs */}
            <TabPanel value={tabIndex} index={0}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateConfig}>
                        Add Config
                    </Button>
                </Box>
                {configsLoading ? <CircularProgress /> : (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Scope</TableCell>
                                    <TableCell>Agent</TableCell>
                                    <TableCell>Provider</TableCell>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Default</TableCell>
                                    <TableCell>Capabilities</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {configs.map((config: LLMConfig) => (
                                    <TableRow key={config.config_id}>
                                        <TableCell>{getTenantName(config.tenant_id)}</TableCell>
                                        <TableCell>{config.agent_name || 'All agents'}</TableCell>
                                        <TableCell><Chip label={config.provider_name} size="small" /></TableCell>
                                        <TableCell>{config.model_name}</TableCell>
                                        <TableCell>{config.is_default ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="disabled" fontSize="small" />}</TableCell>
                                        <TableCell>{getCapabilityBadges(config.provider_name, config.model_name)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => openEditConfig(config)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => deleteConfigMut.mutate(config.config_id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {configs.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">No configurations yet. Using environment variable defaults.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </TabPanel>

            {/* Tab 1: API Keys */}
            <TabPanel value={tabIndex} index={1}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormKeyTenantId(''); setFormKeyProvider(''); setFormApiKey(''); setKeyDialogOpen(true); }}>
                        Add API Key
                    </Button>
                </Box>
                {keysLoading ? <CircularProgress /> : (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Scope</TableCell>
                                    <TableCell>Provider</TableCell>
                                    <TableCell>Key Hint</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiKeys.map((key: LLMApiKey) => (
                                    <TableRow key={key.key_id}>
                                        <TableCell>{getTenantName(key.tenant_id)}</TableCell>
                                        <TableCell><Chip label={key.provider_name} size="small" /></TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>...{key.key_hint}</TableCell>
                                        <TableCell>{key.created_at ? new Date(key.created_at).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" color="error" onClick={() => deleteKeyMut.mutate(key.key_id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {apiKeys.length === 0 && (
                                    <TableRow><TableCell colSpan={5} align="center">No API keys stored. Using environment variables.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </TabPanel>

            {/* Tab 2: Model Pricing */}
            <TabPanel value={tabIndex} index={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
                        Pricing is used for cost tracking. Add new models or update prices as needed.
                    </Alert>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePricing}>
                        Add Model
                    </Button>
                </Box>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Provider</TableCell>
                                <TableCell>Model</TableCell>
                                <TableCell align="right">Input $/MTok</TableCell>
                                <TableCell align="right">Output $/MTok</TableCell>
                                <TableCell align="right">Context</TableCell>
                                <TableCell>Tools</TableCell>
                                <TableCell>JSON</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pricing.map((p: ModelPricing) => (
                                <TableRow key={p.pricing_id} sx={{ opacity: p.is_active ? 1 : 0.5 }}>
                                    <TableCell><Chip label={p.provider_name} size="small" /></TableCell>
                                    <TableCell>{p.model_name}</TableCell>
                                    <TableCell align="right">
                                        ${p.input_price_per_mtok}
                                        {p.input_price_above_tier != null && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                ${p.input_price_above_tier} above {((p.tier_threshold_tokens || 0) / 1000).toFixed(0)}K
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        ${p.output_price_per_mtok}
                                        {p.output_price_above_tier != null && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                ${p.output_price_above_tier} above {((p.tier_threshold_tokens || 0) / 1000).toFixed(0)}K
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{(p.context_window / 1000).toFixed(0)}K</TableCell>
                                    <TableCell>{p.supports_tools ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="disabled" fontSize="small" />}</TableCell>
                                    <TableCell>{p.supports_json_mode ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="disabled" fontSize="small" />}</TableCell>
                                    <TableCell>{p.is_active ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="disabled" fontSize="small" />}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => openEditPricing(p)}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => deletePricingMut.mutate(p.pricing_id)}><DeleteIcon fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {pricing.length === 0 && (
                                <TableRow><TableCell colSpan={9} align="center">No model pricing entries yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TabPanel>

            {/* Config Dialog */}
            <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingConfig ? 'Edit Configuration' : 'Add Configuration'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    {!editingConfig && (
                        <>
                            <FormControl fullWidth size="small">
                                <InputLabel>Tenant (optional)</InputLabel>
                                <Select value={formTenantId} onChange={(e) => setFormTenantId(e.target.value)} label="Tenant (optional)">
                                    <MenuItem value="">Global</MenuItem>
                                    {tenants.map((t: Tenant) => <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Agent (optional)</InputLabel>
                                <Select value={formAgentName} onChange={(e) => setFormAgentName(e.target.value)} label="Agent (optional)">
                                    <MenuItem value="">All agents</MenuItem>
                                    {AGENT_NAMES.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </>
                    )}
                    <FormControl fullWidth size="small" required>
                        <InputLabel>Provider</InputLabel>
                        <Select value={formProvider} onChange={(e) => setFormProvider(e.target.value)} label="Provider">
                            {PROVIDER_NAMES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={pricing
                            .filter((p: ModelPricing) => p.provider_name === formProvider)
                            .map((p: ModelPricing) => p.model_name)}
                        value={formModel}
                        onInputChange={(_, v) => setFormModel(v)}
                        renderInput={(params) => (
                            <TextField {...params} label="Model Name" required placeholder="e.g. claude-haiku-4-5-20251001" />
                        )}
                    />
                    {formProvider && formModel && getCapabilityBadges(formProvider, formModel)}
                    {capabilityError && <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>{capabilityError}</Alert>}
                    {formAgentName && agentRequirements[formAgentName] && (
                        <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                            Agent <strong>{formAgentName}</strong> requires: {agentRequirements[formAgentName].min_context / 1000}K context
                            {agentRequirements[formAgentName].needs_tools && ', tool calling'}
                            {agentRequirements[formAgentName].needs_json_mode && ', JSON mode'}
                        </Alert>
                    )}
                    <FormControlLabel
                        control={<Switch checked={formIsDefault} onChange={(e) => setFormIsDefault(e.target.checked)} />}
                        label="Set as default"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveConfig} disabled={!formProvider || !formModel || !!capabilityError}>
                        {editingConfig ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* API Key Dialog */}
            <Dialog open={keyDialogOpen} onClose={() => setKeyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add API Key</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Tenant (optional)</InputLabel>
                        <Select value={formKeyTenantId} onChange={(e) => setFormKeyTenantId(e.target.value)} label="Tenant (optional)">
                            <MenuItem value="">Global</MenuItem>
                            {tenants.map((t: Tenant) => <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" required>
                        <InputLabel>Provider</InputLabel>
                        <Select value={formKeyProvider} onChange={(e) => setFormKeyProvider(e.target.value)} label="Provider">
                            {PROVIDER_NAMES.filter(p => p !== 'ollama').map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        label="API Key" size="small" fullWidth required type="password"
                        value={formApiKey} onChange={(e) => setFormApiKey(e.target.value)}
                        placeholder="sk-... or similar"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKeyDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveKey} disabled={!formKeyProvider || !formApiKey}>
                        Save Key
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pricing Dialog */}
            <Dialog open={pricingDialogOpen} onClose={() => setPricingDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPricing ? 'Edit Model Pricing' : 'Add Model Pricing'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <FormControl fullWidth size="small" required disabled={!!editingPricing}>
                        <InputLabel>Provider</InputLabel>
                        <Select value={pricingProvider} onChange={(e) => setPricingProvider(e.target.value)} label="Provider">
                            {PROVIDER_NAMES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Model Name" size="small" fullWidth required
                        disabled={!!editingPricing}
                        value={pricingModel} onChange={(e) => setPricingModel(e.target.value)}
                        placeholder="e.g. gemini-3.1-flash-lite-preview"
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Input $/MTok" size="small" fullWidth required type="number"
                            value={pricingInputPrice} onChange={(e) => setPricingInputPrice(e.target.value)}
                            inputProps={{ step: '0.01', min: '0' }}
                        />
                        <TextField
                            label="Output $/MTok" size="small" fullWidth required type="number"
                            value={pricingOutputPrice} onChange={(e) => setPricingOutputPrice(e.target.value)}
                            inputProps={{ step: '0.01', min: '0' }}
                        />
                    </Box>
                    <TextField
                        label="Context Window (tokens)" size="small" fullWidth required type="number"
                        value={pricingContextWindow} onChange={(e) => setPricingContextWindow(e.target.value)}
                        placeholder="e.g. 1000000"
                    />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                        Tiered Pricing (optional)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Input Above Tier $/MTok" size="small" fullWidth type="number"
                            value={pricingInputAbove} onChange={(e) => setPricingInputAbove(e.target.value)}
                            inputProps={{ step: '0.01', min: '0' }}
                        />
                        <TextField
                            label="Output Above Tier $/MTok" size="small" fullWidth type="number"
                            value={pricingOutputAbove} onChange={(e) => setPricingOutputAbove(e.target.value)}
                            inputProps={{ step: '0.01', min: '0' }}
                        />
                    </Box>
                    <TextField
                        label="Tier Threshold (tokens)" size="small" fullWidth type="number"
                        value={pricingTierThreshold} onChange={(e) => setPricingTierThreshold(e.target.value)}
                        placeholder="e.g. 200000"
                    />
                    <TextField
                        label="Max Output Tokens (optional)" size="small" fullWidth type="number"
                        value={pricingMaxOutput} onChange={(e) => setPricingMaxOutput(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={pricingSupportsTools} onChange={(e) => setPricingSupportsTools(e.target.checked)} />}
                            label="Supports Tools"
                        />
                        <FormControlLabel
                            control={<Switch checked={pricingSupportsJson} onChange={(e) => setPricingSupportsJson(e.target.checked)} />}
                            label="Supports JSON Mode"
                        />
                        <FormControlLabel
                            control={<Switch checked={pricingIsActive} onChange={(e) => setPricingIsActive(e.target.checked)} />}
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPricingDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSavePricing}
                        disabled={!pricingProvider || !pricingModel || !pricingInputPrice || !pricingOutputPrice || !pricingContextWindow}
                    >
                        {editingPricing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!errorMsg}
                autoHideDuration={6000}
                onClose={() => setErrorMsg(null)}
            >
                <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
                    {errorMsg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ModelConfiguration;
